import React, { useState } from 'react';
import { Shield, DollarSign, FileText, Calendar, AlertTriangle } from 'lucide-react';
import { useMessageStore } from '../stores/messageStore';
import { formatPrice } from '../utils/formatters';

interface ApprovalConfirmationProps {
  request: any;
  onConfirm: (data: {
    securityDeposit: number;
    moveInInstructions: string;
    houseRules: string;
  }) => void;
  onCancel: () => void;
}

const ApprovalConfirmation: React.FC<ApprovalConfirmationProps> = ({
  request,
  onConfirm,
  onCancel
}) => {
  const { addMessage } = useMessageStore();
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    securityDeposit: request.listing?.price || 0,
    moveInInstructions: '',
    houseRules: ''
  });

  const serviceFee = Math.round((request.listing?.price || 0) * 0.05);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const systemMessage = `
🎉 Application Approved!

Next Steps:
1. Tenant will pay ${formatPrice(serviceFee)} good faith deposit (5% of first month's rent)
2. This deposit is kept by SubleaseHero as a service fee
3. Contact information will be shared after deposit payment
4. Security deposit of ${formatPrice(formData.securityDeposit)} to be handled directly between parties

Property Details:
📍 ${request.listing.title}
📅 Move-in: ${request.moveInDate}
💰 Monthly Rent: ${formatPrice(request.listing.price)}

Move-in Instructions:
${formData.moveInInstructions}

House Rules:
${formData.houseRules}
`;

      if (request.conversationId) {
        addMessage(request.conversationId, {
          senderId: 'system',
          content: systemMessage,
          isRead: false
        });
      }

      onConfirm(formData);
    } catch (error) {
      console.error('Error processing approval:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md overflow-y-auto max-h-[85vh]">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-900">Approve Application</h2>
          <p className="text-sm text-gray-600">Set move-in details and house rules</p>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Service Fee Notice */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
            <div className="flex items-start">
              <DollarSign className="h-5 w-5 text-indigo-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-indigo-800">Good Faith Deposit</h4>
                <p className="mt-1 text-sm text-indigo-700">
                  After approval, tenant will pay {formatPrice(serviceFee)} (5% of first month's rent) as a non-refundable service fee to SubleaseHero.
                </p>
              </div>
            </div>
          </div>

          {/* Security Deposit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Security Deposit
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="number"
                value={formData.securityDeposit}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  securityDeposit: parseInt(e.target.value) || 0
                }))}
                className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              To be collected directly from tenant
            </p>
          </div>

          {/* Move-in Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Move-in Instructions
            </label>
            <textarea
              value={formData.moveInInstructions}
              onChange={(e) => setFormData(prev => ({ ...prev, moveInInstructions: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="Key pickup details, parking information, etc."
              required
            />
          </div>

          {/* House Rules */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              House Rules
            </label>
            <textarea
              value={formData.houseRules}
              onChange={(e) => setFormData(prev => ({ ...prev, houseRules: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm"
              placeholder="Quiet hours, guest policy, etc."
              required
            />
          </div>

          {/* Important Notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-yellow-800">Important Information</h4>
                <ul className="mt-1 text-xs text-yellow-700 space-y-1">
                  <li>• SubleaseHero keeps the good faith deposit as a service fee</li>
                  <li>• Contact details shared after deposit payment</li>
                  <li>• Security deposit and rent handled directly between parties</li>
                  <li>• We recommend creating a formal sublease agreement</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing}
              className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <Shield className="h-4 w-4" />
                  <span>Approve Application</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApprovalConfirmation;